import type { Resources } from '@somnhold/content';
import { builtMaxHp, maxCharges, parseHexKey, repairPriority, repairQuote, sortedCellKeys, type CampaignState, type PlayerState, type RepairQuote } from '@somnhold/sim';

export interface Damaged {
  key: string;
  q: number;
  r: number;
  structure: string;
  /** What is left, 0..1 (HP for buildings, charges for traps). */
  left: number;
  ruined: boolean;
  quote: RepairQuote;
}

/** Everything broken that nobody has ordered mended yet, in the order to repair it: gates, walls, towers, the rest; worst first. */
export const damagedList = (s: CampaignState, p: PlayerState, now: number): Damaged[] => {
  const out: Damaged[] = [];
  for (const key of sortedCellKeys(s)) {
    const cell = s.cells[key];
    const b = cell?.built;
    if (!b || cell.job) continue;
    const quote = repairQuote(s, key, p, now);
    if (!quote) continue;
    const max = builtMaxHp(b, now);
    const mc = maxCharges(b);
    const left = max > 0 ? b.hp / max : mc > 0 ? b.charges / mc : 1;
    const { q, r } = parseHexKey(key);
    out.push({ key, q, r, structure: b.structure, left, ruined: max > 0 && b.hp <= 0, quote });
  }
  return out.sort((a, b) => repairPriority(a.structure) - repairPriority(b.structure) || a.left - b.left || (a.key < b.key ? -1 : 1));
};

const PAY = ['wood', 'stone', 'crystal', 'iron'] as const;

/** «Repair all»: in priority order, every repair the store can still pay after the ones before it. */
export const planRepairs = (list: Damaged[], storage: Resources): { picked: Damaged[]; skipped: Damaged[]; cost: Resources } => {
  const left = { ...storage };
  const cost: Resources = { wood: 0, stone: 0, crystal: 0, dust: 0, iron: 0 };
  const picked: Damaged[] = [];
  const skipped: Damaged[] = [];
  for (const d of list) {
    if (PAY.every((k) => (left[k] ?? 0) >= (d.quote.cost[k] ?? 0))) {
      for (const k of PAY) {
        left[k] = (left[k] ?? 0) - (d.quote.cost[k] ?? 0);
        cost[k] = (cost[k] ?? 0) + (d.quote.cost[k] ?? 0);
      }
      picked.push(d);
    } else skipped.push(d);
  }
  return { picked, skipped, cost };
};
